import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { IMAGES } from "@/constants/images";
import PageHero from "@/components/public/PageHero";
import { toast } from "sonner";
import { CheckCircle2, Upload, Loader2 } from "lucide-react";

const UNITS = ["MT", "KG", "BAGS", "FCL (20ft)", "FCL (40ft)"];

const initial = {
  full_name: "", company: "", country: "", email: "", phone: "",
  product: "", origin: "", process: "", quantity: "", unit: "MT",
  packaging: "", delivery_date: "", destination_port: "", message: "",
  additional_requirements: "", request_sample: false, request_price: true,
  request_spec: false, consent: false, attachment: "",
};

export default function RequestQuote() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const product = params.get("product");
    const sample = params.get("sample");
    setForm((f) => ({ ...f, product: product || f.product, request_sample: sample === "1" ? true : f.request_sample }));
  }, [params]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/rfq-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("attachment", res.data.url);
      toast.success("File attached");
    } catch (err) { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please agree to be contacted about this inquiry."); return; }
    if (!form.full_name || !form.company || !form.country || !form.email || !form.product || !form.quantity) {
      toast.error("Please fill in all required fields."); return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/rfq", form);
      setDone(res.data.rfq_number);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div>
        <PageHero eyebrow="Request a Quote" title="Inquiry Received" image={IMAGES.greenBeans} />
        <section className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-8">
          <CheckCircle2 className="mx-auto h-16 w-16 text-forest" />
          <h2 className="mt-6 font-display text-2xl font-extrabold text-forest-dark">Thank You</h2>
          <p className="mt-3 font-body text-base leading-relaxed text-espresso/70">{t("quote.thanks")}</p>
          <div className="mt-6 inline-block rounded-lg border border-forest/15 bg-beige px-6 py-4">
            <div className="font-mono text-[11px] uppercase tracking-wider text-espresso/50">Your Reference</div>
            <div className="mt-1 font-display text-2xl font-extrabold text-forest" data-testid="rfq-number-result">{done}</div>
          </div>
          <div className="mt-8"><button onClick={() => { setForm(initial); setDone(null); }} className="rounded-full border border-forest/25 px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-forest hover:bg-forest/5">Submit Another</button></div>
        </section>
      </div>
    );
  }

  const inputCls = "w-full rounded-lg border border-forest/15 bg-white px-4 py-3 font-body text-sm text-espresso outline-none transition-colors focus:border-forest/50";
  const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-espresso/60";

  return (
    <div>
      <PageHero eyebrow="B2B Inquiry" title="Request a Quote" sub="Tell us what you are looking for and our team will respond with availability and commercial details." image={IMAGES.greenBeans} />
      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-8">
        <form onSubmit={submit} data-testid="rfq-form" className="space-y-8 rounded-2xl border border-forest/10 bg-white p-6 sm:p-10">
          <div>
            <h3 className="mb-4 font-display text-lg font-bold text-forest-dark">Your Details</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><label className={labelCls}>Full Name *</label><input data-testid="rfq-full-name" className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required /></div>
              <div><label className={labelCls}>Company Name *</label><input data-testid="rfq-company" className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} required /></div>
              <div><label className={labelCls}>Country *</label><input data-testid="rfq-country" className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} required /></div>
              <div><label className={labelCls}>Email *</label><input type="email" data-testid="rfq-email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} required /></div>
              <div className="sm:col-span-2"><label className={labelCls}>WhatsApp / Phone</label><input data-testid="rfq-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 font-display text-lg font-bold text-forest-dark">Coffee Requirements</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><label className={labelCls}>Coffee Product *</label><input data-testid="rfq-product" className={inputCls} placeholder="e.g. Indonesian Arabica Ateng" value={form.product} onChange={(e) => set("product", e.target.value)} required /></div>
              <div><label className={labelCls}>Origin</label><input data-testid="rfq-origin" className={inputCls} value={form.origin} onChange={(e) => set("origin", e.target.value)} /></div>
              <div><label className={labelCls}>Process</label><input data-testid="rfq-process" className={inputCls} value={form.process} onChange={(e) => set("process", e.target.value)} /></div>
              <div><label className={labelCls}>Required Quantity *</label><input data-testid="rfq-quantity" className={inputCls} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} required /></div>
              <div><label className={labelCls}>Unit *</label><select data-testid="rfq-unit" className={inputCls} value={form.unit} onChange={(e) => set("unit", e.target.value)}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</select></div>
              <div><label className={labelCls}>Preferred Packaging</label><input data-testid="rfq-packaging" className={inputCls} value={form.packaging} onChange={(e) => set("packaging", e.target.value)} /></div>
              <div><label className={labelCls}>Target Delivery Date</label><input type="date" data-testid="rfq-delivery-date" className={inputCls} value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Destination Port</label><input data-testid="rfq-destination" className={inputCls} value={form.destination_port} onChange={(e) => set("destination_port", e.target.value)} /></div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div><label className={labelCls}>Message</label><textarea data-testid="rfq-message" rows={3} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} /></div>
            <div><label className={labelCls}>Additional Requirements</label><textarea data-testid="rfq-additional" rows={2} className={inputCls} value={form.additional_requirements} onChange={(e) => set("additional_requirements", e.target.value)} /></div>
            <div>
              <label className={labelCls}>Upload File</label>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-forest/25 bg-beige/50 px-4 py-3 font-body text-sm text-espresso/60 hover:border-forest/50">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {form.attachment ? "File attached — replace" : "Attach a specification or document"}
                <input type="file" data-testid="rfq-file" className="hidden" onChange={onFile} />
              </label>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 rounded-lg bg-beige/60 p-4">
            {[["request_sample", "Request Sample"], ["request_price", "Request Price"], ["request_spec", "Request Specification Sheet"]].map(([k, lbl]) => (
              <label key={k} className="flex cursor-pointer items-center gap-2 font-body text-sm text-espresso/80">
                <input type="checkbox" data-testid={`rfq-${k.replace("_","-")}`} checked={form[k]} onChange={(e) => set(k, e.target.checked)} className="h-4 w-4 accent-forest" />{lbl}
              </label>
            ))}
          </div>

          <label className="flex cursor-pointer items-start gap-3 font-body text-sm text-espresso/75">
            <input type="checkbox" data-testid="rfq-consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 h-4 w-4 accent-forest" />
            I agree that Warehouse Coffee may contact me regarding this inquiry.
          </label>

          <button type="submit" data-testid="rfq-submit-button" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-offwhite transition-all hover:bg-forest-dark disabled:opacity-60">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Send RFQ"}
          </button>
        </form>
      </section>
    </div>
  );
}
